import axios from 'axios';
import { ProviderContext } from 'notistack';

// Reference: Zendesk status codes and guidance
// https://developer.zendesk.com/api-reference/introduction/requests/

// Status code responses either from the API or from the gateway layer

const CONVERSATION_STATUS_MESSAGE_MAP: Record<number, string> = {
    0: 'Connection lost. Please check your connection and try again.',
    400: "We couldn't process that. Please try again soon.",
    // 401 and 403 are handled in the gateway layer so likely won't be hit
    401: 'Session expired. Please refresh the page and try again.',
    403: 'Session expired. Please refresh the page and try again.',
    409: 'This conversation was updated elsewhere. Please refresh and try again.',
    413: 'Message or files too large. Please reduce content size.',
    422: "We're having a temporary issue. Please try again soon.",
    429: 'Too many attempts. Wait a moment and try again.',
    500: "We're having a temporary issue. Please try again soon.",
};

const UPLOAD_STATUS_CODE_MESSAGE_MAP: Record<number, string> = {
    0: 'Upload failed. Please check your connection and try again.',
    400: "We're having a temporary issue. Try uploading your document again soon.",
    401: 'Session expired. Please refresh the page and try again.',
    403: 'Session expired. Please refresh the page and try again.',
    413: 'File too large. Maximum size is 50MB per file.',
    415: 'File type not supported. Please use any image type, video type, PDF, CSV, XLS, XLSX, DOC, or DOCX.',
    422: "We're having a temporary issue. Try uploading your document again soon.",
    429: 'Too many uploads. Please wait a moment and try again.',
    500: "We're having a temporary issue. Try uploading your document again soon.",
};

interface UploadErrorHandlerOptions {
    enqueueSnackbar: ProviderContext['enqueueSnackbar'];
}

export function mapZendeskErrorToMessage(error: unknown): string {
    if (axios.isAxiosError(error)) {
        const status = error.response?.status;

        if (!status) {
            return CONVERSATION_STATUS_MESSAGE_MAP[0];
        }

        if (CONVERSATION_STATUS_MESSAGE_MAP[status]) {
            return CONVERSATION_STATUS_MESSAGE_MAP[status];
        }

        if (status >= 500) {
            return CONVERSATION_STATUS_MESSAGE_MAP[500];
        }

        const apiMsg = (error.response?.data as any)?.message || (error.response?.data as any)?.error || error.message;
        return apiMsg || 'Unexpected error. Please try again.';
    }

    return 'Unexpected error. Please try again.';
}

/**
 * Handles upload errors from Uppy upload results with user-friendly error messages
 * @param result - The result from uppy.upload()
 * @param options - Configuration options including snackbar function
 * @returns true if there were errors (should stop flow), false if successful
 */
export function handleUploadErrors(result: unknown, { enqueueSnackbar }: UploadErrorHandlerOptions): boolean {
    type FailedShape = { name?: string; data?: { name?: string }; response?: { status?: number } };
    type ResultShape = { failed?: unknown[] };

    const getUploadErrorMessage = (statusCode?: number): string => {
        const s = statusCode ?? 0;
        if (UPLOAD_STATUS_CODE_MESSAGE_MAP[s]) {
            return UPLOAD_STATUS_CODE_MESSAGE_MAP[s];
        }
        if (s >= 500) {
            return UPLOAD_STATUS_CODE_MESSAGE_MAP[500];
        }
        return 'Upload failed. Please try again.';
    };

    // Defensive guard: Upload can fail in ways that return undefined/null
    // (user cancellation, network interruption, plugin errors, memory issues).
    // Handle gracefully instead of crashing when accessing result properties.
    if (!result) {
        enqueueSnackbar('File upload failed. Please try again.', { variant: 'error' });
        return true;
    }

    const failed = Array.isArray((result as ResultShape).failed) ? ((result as ResultShape).failed as unknown[]) : [];

    if (failed.length > 0) {
        failed.forEach((f) => {
            const status = (f as FailedShape)?.response?.status;
            const name = (f as FailedShape)?.name || (f as FailedShape)?.data?.name || 'File';
            const msg = getUploadErrorMessage(status);
            enqueueSnackbar(`${name}: ${msg}`, { variant: 'error' });
        });

        // Show summary alert for failed uploads
        enqueueSnackbar(
            failed.length === 1
                ? 'File upload failed. Please try again.'
                : 'Some files failed to upload. Please try again.',
            { variant: 'error' }
        );

        return true; // Has errors
    }

    return false; // No errors
}

export default mapZendeskErrorToMessage;
